import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { Settings, MessageSquare, BarChart3, Save, RefreshCw, Eye } from 'lucide-react';
import Login from './Login';
import ConversationList from './ConversationList';
import SystemPromptEditor from './SystemPromptEditor';
import AnalyticsPanel from './AnalyticsPanel';

const PROMPT_SECTIONS = [
  { key: 'personality', label: 'Personality', placeholder: 'Describe the assistant\'s personality, e.g. pragmatic, direct, creative...' },
  { key: 'tone', label: 'Tone', placeholder: 'Describe the tone, e.g. clear, concise, honest, no jargon...' },
  { key: 'rules', label: 'Rules', placeholder: 'List rules, e.g. never make up facts, cite real projects, etc.' },
  { key: 'knowledge', label: 'Knowledge', placeholder: 'Add key facts, links, or markdown tables here.' },
  { key: 'faq', label: 'FAQ', placeholder: 'Add common Q&A, e.g. "Are you available for freelance?"' }
];

function AdminDashboard() {
  const [conversations, setConversations] = useState([]);
  const [sections, setSections] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [activeTab, setActiveTab] = useState('conversations');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (authenticated && activeTab === 'conversations') {
      fetchData(page);
    }
    // eslint-disable-next-line
  }, [page, authenticated, activeTab]);

  const checkAuth = async () => {
    try {
      // Use a simpler endpoint for auth check to avoid race conditions
      await api.get('/api/admin/system-prompt');
      setAuthenticated(true);
      fetchData(1);
    } catch {
      setAuthenticated(false);
    }
  };

  const fetchData = async (pageToFetch = 1) => {
    setLoading(true);
    try {
      const [conversationsRes, promptRes, analyticsRes] = await Promise.all([
        api.get(`/api/admin/conversations?page=${pageToFetch}&limit=10`),
        api.get('/api/admin/system-prompt'),
        api.get('/api/admin/analytics')
      ]);
      setConversations(conversationsRes.data.conversations);
      setTotalPages(conversationsRes.data.totalPages || 1);
      setSections(promptRes.data.sections || {});
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      setMessage('Failed to fetch data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetail = async (id) => {
    setDetailLoading(true);
    setConversationDetail(null);
    try {
      const res = await api.get(`/api/admin/conversations/${id}`);
      setConversationDetail(res.data);
    } catch (e) {
      setConversationDetail({ error: 'Failed to load conversation.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const openConversation = (id) => {
    setSelectedConversation(id);
    fetchConversationDetail(id);
  };

  const closeConversation = () => {
    setSelectedConversation(null);
    setConversationDetail(null);
  };



  const updateSystemPrompt = async () => {
    setLoading(true);
    try {
      await api.put('/api/admin/system-prompt', { sections });
      setMessage('System prompt updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Failed to update system prompt');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.post('/api/admin/logout');
    setAuthenticated(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text, maxLength = 100) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleDeleteConversation = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.delete(`/api/admin/conversations/${id}`);
      setMessage('Conversation deleted.');
      fetchData();
    } catch (e) {
      setMessage('Failed to delete conversation.');
    }
  };

  const toggleSelectConversation = (id) => {
    setSelectedConversations(selected =>
      selected.includes(id)
        ? selected.filter(cid => cid !== id)
        : [...selected, id]
    );
  };

  const selectAllConversations = () => {
    setSelectedConversations(conversations.map(c => c._id));
  };

  const deselectAllConversations = () => {
    setSelectedConversations([]);
  };

  const handleBulkDelete = async () => {
    if (selectedConversations.length === 0) return;
    if (!window.confirm(`Delete ${selectedConversations.length} conversations?`)) return;
    try {
      await api.post('/api/admin/conversations/bulk-delete', { ids: selectedConversations });
      setMessage('Conversations deleted.');
      setSelectedConversations([]);
      fetchData();
    } catch (e) {
      setMessage('Failed to delete conversations.');
    }
  };

  if (!authenticated) {
    return <Login onLogin={() => { setAuthenticated(true); fetchData(); }} />;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={fetchData} disabled={loading} className="refresh-btn">
              <RefreshCw size={16} />
              Refresh
            </button>
            <button onClick={handleLogout} className="refresh-btn" style={{ background: '#e74c3c' }}>
              Logout
            </button>
          </div>
        </div>
        {message && <div className="message-banner">{message}</div>}
      </header>

      <nav className="admin-nav">
        <button 
          onClick={() => setActiveTab('conversations')} 
          className={`nav-btn ${activeTab === 'conversations' ? 'active' : ''}`}
        >
          <MessageSquare size={16} />
          Conversations
        </button>
        <button 
          onClick={() => setActiveTab('system-prompt')} 
          className={`nav-btn ${activeTab === 'system-prompt' ? 'active' : ''}`}
        >
          <Settings size={16} />
          System Prompt
        </button>
        <button 
          onClick={() => setActiveTab('analytics')} 
          className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`}
        >
          <BarChart3 size={16} />
          Analytics
        </button>
      </nav>

      <main className="admin-content">
        {loading && <div className="loading-overlay">Loading...</div>}

        {activeTab === 'conversations' && (
          <>
            <ConversationList
              conversations={conversations}
              selectedConversations={selectedConversations}
              toggleSelectConversation={toggleSelectConversation}
              selectAllConversations={selectAllConversations}
              deselectAllConversations={deselectAllConversations}
              handleBulkDelete={handleBulkDelete}
              handleDeleteConversation={handleDeleteConversation}
              loading={loading}
              onViewConversation={openConversation}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, gap: 10 }}>
              <button className="refresh-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
              <span style={{ alignContent: 'center' }}>Page {page} of {totalPages}</span>
              <button className="refresh-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
            </div>
          </>
        )}

        {activeTab === 'system-prompt' && (
          <SystemPromptEditor
            sections={sections}
            setSections={setSections}
            handleSavePrompt={updateSystemPrompt}
            loading={loading}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsPanel analytics={analytics} loading={loading} />
        )}

        {/* Conversation Detail Modal */}
        {selectedConversation && (
          <div className="modal-overlay" onClick={closeConversation}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={closeConversation}>&times;</button>
              <h2>Conversation Details</h2>
              {detailLoading && <div>Loading...</div>}
              {conversationDetail && !detailLoading && !conversationDetail.error && (
                <div>
                  <div style={{ marginBottom: 10, color: '#666', fontSize: 14 }}>
                    <div><strong>Started:</strong> {formatDate(conversationDetail.createdAt)}</div>
                    <div><strong>IP:</strong> {conversationDetail.ipAddress || 'Unknown'}</div>
                    <div><strong>User Agent:</strong> {conversationDetail.userAgent || 'Unknown'}</div>
                  </div>
                  <div className="conversation-messages">
                    {conversationDetail.messages.map((msg, idx) => (
                      <div key={idx} className={`conv-msg ${msg.role}`}>
                        <div className="conv-msg-meta">
                          <span className="conv-msg-role">{msg.role}</span>
                          <span className="conv-msg-time">{msg.timestamp ? formatDate(msg.timestamp) : ''}</span>
                        </div>
                        <div className="conv-msg-content">{msg.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {conversationDetail && conversationDetail.error && (
                <div style={{ color: 'red' }}>{conversationDetail.error}</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard; 